// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/MercadoLivre.tsx
//  Integração com o Mercado Livre: dashboard, anúncios, vendas, perguntas e configuração.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import {
  ShoppingCart, BarChart3, Package, MessageSquare, Settings,
  RefreshCw, Link, Loader2, CheckCircle2, XCircle, ExternalLink,
  Download, Send,
} from 'lucide-react';
import { Button }      from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input }       from '@/components/ui/input';
import { Label }       from '@/components/ui/label';
import { Badge }       from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox }    from '@/components/ui/checkbox';
import { Textarea }    from '@/components/ui/textarea';
import { useAuth }     from '@/hooks/useAuth';
import { apiClient }   from '@/lib/apiClient';
import { toast }       from '@/components/ui/sonner';

// ── Tipos ─────────────────────────────────────────────────────────────────

interface ConfigML {
  empresaId: number;
  clienteId: string | null;
  clientSecret: string | null;
  redirectUri: string | null;
  notificationUrl: string | null;
  userId: string | null;
  nickname: string | null;
  ativo: boolean;
  conectado: boolean;
  dtExpiraToken: string;
}

interface DashboardML {
  totalAnuncios: number;
  anunciosAtivos: number;
  anunciosPausados: number;
  totalVendas: number;
  vendasPendentes: number;
  vendasEnviadas: number;
  vendasEntregues: number;
  perguntasPendentes: number;
  faturamentoTotal: number;
  taxasML: number;
  faturamentoLiquido: number;
}

interface AnuncioML {
  id: number;
  itemId: string | null;
  titulo: string | null;
  status: string | null;
  preco: number;
  estoque: number;
  sincronizado: boolean;
  permalink: string | null;
  dtAtualizacao: string;
}

interface VendaML {
  id: number;
  orderId: string | null;
  buyerNickname: string | null;
  buyerNome: string | null;
  valorTotal: number;
  valorFrete: number;
  valorTaxaML: number;
  statusPedido: string | null;
  statusEnvio: string | null;
  dtVenda: string;
  cidadeEntrega: string | null;
  ufEntrega: string | null;
}

interface PerguntaML {
  id: number;
  perguntaId: string | null;
  itemId: string | null;
  pergunta: string | null;
  resposta: string | null;
  status: string | null;
  dtPergunta: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────

const statusBadge = (status: string | null) => {
  switch (status) {
    case 'active':    return <Badge className="bg-green-100 text-green-800 border-green-200">Ativo</Badge>;
    case 'paused':    return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Pausado</Badge>;
    case 'paid':      return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Pago</Badge>;
    case 'shipped':   return <Badge className="bg-purple-100 text-purple-800 border-purple-200">Enviado</Badge>;
    case 'delivered': return <Badge className="bg-green-100 text-green-800 border-green-200">Entregue</Badge>;
    case 'cancelled': return <Badge variant="destructive">Cancelado</Badge>;
    case 'UNANSWERED':return <Badge variant="destructive">Pendente</Badge>;
    case 'ANSWERED':  return <Badge className="bg-green-100 text-green-800 border-green-200">Respondida</Badge>;
    default:          return <Badge variant="outline">{status ?? '—'}</Badge>;
  }
};

const fmt = (n: number) => n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ── Tab: Configuração ─────────────────────────────────────────────────────

function TabConfig({ empresaId }: { empresaId: number }) {
  const [config, setConfig] = useState<ConfigML | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [form, setForm] = useState({
    clienteId: '', clientSecret: '', redirectUri: 'https://udisys.com.br/callback',
    notificationUrl: '', ativo: true,
  });
  const [code, setCode] = useState('');
  const [authorizing, setAuthorizing] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<ConfigML>(`/api/mercadolivre/config/${empresaId}`);
      setConfig(data);
      setForm({
        clienteId:       data.clienteId ?? '',
        clientSecret:    data.clientSecret ?? '',
        redirectUri:     data.redirectUri ?? 'https://udisys.com.br/callback',
        notificationUrl: data.notificationUrl ?? '',
        ativo:           data.ativo,
      });
    } catch { toast.error('Erro ao carregar configuração.'); }
    finally  { setLoading(false); }
  };

  const salvar = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/mercadolivre/config', { empresaId, ...form });
      toast.success('Configuração salva!');
      carregar();
    } catch { toast.error('Erro ao salvar.'); }
    finally  { setSaving(false); }
  };

  const abrirAutorizacao = async () => {
    const { data } = await apiClient.get<{ authUrl: string }>('/api/mercadolivre/auth-url', {
      params: { clientId: form.clienteId, redirectUri: form.redirectUri },
    });
    window.open(data.authUrl, '_blank');
  };

  const autorizar = async () => {
    if (!code.trim()) { toast.error('Cole o código de autorização.'); return; }
    setAuthorizing(true);
    try {
      await apiClient.post('/api/mercadolivre/token/autorizar', {
        empresaId, code: code.trim(), redirectUri: form.redirectUri,
      });
      toast.success('Autorizado com sucesso!');
      setCode('');
      carregar();
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro na autorização.';
      toast.error('Erro', { description: msg });
    } finally { setAuthorizing(false); }
  };

  const renovar = async () => {
    try {
      await apiClient.post('/api/mercadolivre/token/renovar', { empresaId });
      toast.success('Token renovado!');
      carregar();
    } catch { toast.error('Falha ao renovar token.'); }
  };

  const testar = async () => {
    try {
      const { data } = await apiClient.post<{ sucesso: boolean; mensagem: string; nickname: string }>(
        '/api/mercadolivre/testar-conexao', { empresaId });
      if (data.sucesso) toast.success(`Conexão OK — ${data.nickname}`);
      else toast.error('Falha', { description: data.mensagem });
    } catch { toast.error('Erro ao testar conexão.'); }
  };

  if (!config && !loading) {
    return (
      <div className="space-y-4">
        <Button onClick={carregar}>Carregar configuração</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {loading && <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Carregando...</div>}

      {/* Status da conexão */}
      {config && (
        <Card className={config.conectado ? 'border-green-200 bg-green-50/40' : 'border-red-200 bg-red-50/40'}>
          <CardContent className="pt-4 flex items-center gap-3">
            {config.conectado
              ? <CheckCircle2 className="h-5 w-5 text-green-600" />
              : <XCircle className="h-5 w-5 text-red-500" />}
            <div>
              <p className="font-medium">{config.conectado ? 'Conectado' : 'Desconectado'}</p>
              {config.nickname && <p className="text-sm text-muted-foreground">Conta: {config.nickname} (ID: {config.userId})</p>}
              {config.conectado && (
                <p className="text-xs text-muted-foreground">
                  Token expira em: {new Date(config.dtExpiraToken).toLocaleString('pt-BR')}
                </p>
              )}
            </div>
            {config.conectado && (
              <div className="ml-auto flex gap-2">
                <Button size="sm" variant="outline" onClick={renovar}>Renovar Token</Button>
                <Button size="sm" variant="outline" onClick={testar}>Testar Conexão</Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Credenciais */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Client ID</Label>
          <Input value={form.clienteId} onChange={e => setForm(f => ({ ...f, clienteId: e.target.value }))} />
        </div>
        <div className="space-y-1">
          <Label>Client Secret</Label>
          <Input type="password" value={form.clientSecret} onChange={e => setForm(f => ({ ...f, clientSecret: e.target.value }))} />
        </div>
        <div className="space-y-1">
          <Label>Redirect URI</Label>
          <Input value={form.redirectUri} onChange={e => setForm(f => ({ ...f, redirectUri: e.target.value }))} />
        </div>
        <div className="space-y-1">
          <Label>Notification URL (Webhook)</Label>
          <Input value={form.notificationUrl} onChange={e => setForm(f => ({ ...f, notificationUrl: e.target.value }))} />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="ativo" checked={form.ativo} onCheckedChange={v => setForm(f => ({ ...f, ativo: Boolean(v) }))} />
        <Label htmlFor="ativo">Integração ativa</Label>
      </div>

      <div className="flex gap-2">
        <Button onClick={salvar} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Salvar
        </Button>
      </div>

      {/* Autorização OAuth */}
      <Card>
        <CardHeader><CardTitle className="text-base">Autorização OAuth</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            1. Salve as credenciais acima. 2. Clique em "Abrir Autorização" e autorize no navegador.
            3. Cole o <code>code</code> retornado na URL de callback abaixo.
          </p>
          <Button variant="outline" className="gap-2" onClick={abrirAutorizacao}
            disabled={!form.clienteId}>
            <Link className="h-4 w-4" />Abrir Autorização no ML
          </Button>
          <div className="flex gap-2">
            <Input
              placeholder="Cole o code da URL de callback aqui..."
              value={code} onChange={e => setCode(e.target.value)}
            />
            <Button onClick={autorizar} disabled={authorizing || !code}>
              {authorizing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Autorizar'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Dashboard ────────────────────────────────────────────────────────

function TabDashboard({ empresaId }: { empresaId: number }) {
  const [dash, setDash] = useState<DashboardML | null>(null);
  const [loading, setLoading] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<DashboardML>(`/api/mercadolivre/dashboard/${empresaId}`);
      setDash(data);
    } catch { toast.error('Erro ao carregar dashboard.'); }
    finally  { setLoading(false); }
  };

  if (!dash) return (
    <div className="space-y-4">
      <Button onClick={carregar} disabled={loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Carregar Dashboard
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={carregar} disabled={loading} className="gap-1">
          <RefreshCw className="h-3 w-3" />Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Anúncios Ativos',   value: dash.anunciosAtivos,   sub: `${dash.anunciosPausados} pausados`, color: 'text-blue-600' },
          { label: 'Total de Vendas',   value: dash.totalVendas,       sub: `${dash.vendasPendentes} pendentes`, color: 'text-green-600' },
          { label: 'Perguntas Pend.',   value: dash.perguntasPendentes, sub: 'sem resposta',                    color: dash.perguntasPendentes > 0 ? 'text-red-600' : 'text-green-600' },
          { label: 'Vendas Entregues',  value: dash.vendasEntregues,   sub: `${dash.vendasEnviadas} enviadas`, color: 'text-purple-600' },
        ].map(card => (
          <Card key={card.label}>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">{card.label}</p>
              <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
              <p className="text-xs text-muted-foreground mt-1">{card.sub}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Financeiro</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm text-muted-foreground">Faturamento Total</p>
              <p className="text-xl font-bold text-green-600">{fmt(dash.faturamentoTotal)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Taxas ML</p>
              <p className="text-xl font-bold text-red-500">{fmt(dash.taxasML)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Líquido</p>
              <p className="text-xl font-bold">{fmt(dash.faturamentoLiquido)}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Anúncios ─────────────────────────────────────────────────────────

function TabAnuncios({ empresaId }: { empresaId: number }) {
  const [anuncios, setAnuncios] = useState<AnuncioML[]>([]);
  const [loading, setLoading]   = useState(false);
  const [syncing, setSyncing]   = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<AnuncioML[]>(`/api/mercadolivre/anuncios/${empresaId}`);
      setAnuncios(data);
    } catch { toast.error('Erro ao carregar anúncios.'); }
    finally  { setLoading(false); }
  };

  const sincronizar = async () => {
    setSyncing(true);
    try {
      const { data } = await apiClient.post<{ sincronizados: number; erros: number }>(
        '/api/mercadolivre/anuncios/sincronizar', { empresaId });
      toast.success(`Sincronizado! ${data.sincronizados} produto(s). ${data.erros} erro(s).`);
      carregar();
    } catch { toast.error('Erro ao sincronizar.'); }
    finally  { setSyncing(false); }
  };

  const alterarStatus = async (itemId: string, statusAtual: string | null) => {
    const novoStatus = statusAtual === 'active' ? 'paused' : 'active';
    try {
      await apiClient.put('/api/mercadolivre/anuncios/status', { empresaId, itemId, novoStatus });
      toast.success(`Anúncio ${novoStatus === 'paused' ? 'pausado' : 'ativado'}!`);
      carregar();
    } catch { toast.error('Erro ao alterar status.'); }
  };

  if (!anuncios.length && !loading) return (
    <div className="space-y-4">
      <Button onClick={carregar}>Carregar Anúncios</Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={carregar} disabled={loading} className="gap-1">
          <RefreshCw className="h-3 w-3" />Atualizar
        </Button>
        <Button size="sm" onClick={sincronizar} disabled={syncing} className="gap-1">
          {syncing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
          Sincronizar Estoque
        </Button>
      </div>

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {['Item ID', 'Título', 'Status', 'Preço', 'Estoque', 'Sinc.', 'Ações'].map(h => (
                <th key={h} className="text-left px-3 py-2 font-medium text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {anuncios.map(a => (
              <tr key={a.id} className="border-t hover:bg-muted/30">
                <td className="px-3 py-2 font-mono text-xs">{a.itemId ?? '—'}</td>
                <td className="px-3 py-2 max-w-[200px] truncate">{a.titulo ?? '—'}</td>
                <td className="px-3 py-2">{statusBadge(a.status)}</td>
                <td className="px-3 py-2">{fmt(a.preco)}</td>
                <td className="px-3 py-2">{a.estoque}</td>
                <td className="px-3 py-2">
                  {a.sincronizado
                    ? <CheckCircle2 className="h-4 w-4 text-green-500" />
                    : <XCircle className="h-4 w-4 text-muted-foreground" />}
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    {a.itemId && (
                      <Button size="sm" variant="ghost" className="h-7 px-2"
                        onClick={() => alterarStatus(a.itemId!, a.status)}>
                        {a.status === 'active' ? 'Pausar' : 'Ativar'}
                      </Button>
                    )}
                    {a.permalink && (
                      <Button size="sm" variant="ghost" className="h-7 px-2"
                        onClick={() => window.open(a.permalink!, '_blank')}>
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab: Vendas ───────────────────────────────────────────────────────────

function TabVendas({ empresaId }: { empresaId: number }) {
  const [vendas,    setVendas]    = useState<VendaML[]>([]);
  const [loading,   setLoading]   = useState(false);
  const [importing, setImporting] = useState(false);
  const [dtInicio,  setDtInicio]  = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  });
  const [dtFim,     setDtFim]     = useState(() => new Date().toISOString().slice(0, 10));
  const [status,    setStatus]    = useState('todos');

  const carregar = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { dtInicio, dtFim };
      if (status !== 'todos') params.status = status;
      const { data } = await apiClient.get<VendaML[]>(`/api/mercadolivre/vendas/${empresaId}`, { params });
      setVendas(data);
    } catch { toast.error('Erro ao carregar vendas.'); }
    finally  { setLoading(false); }
  };

  const importar = async () => {
    setImporting(true);
    try {
      const { data } = await apiClient.post<{ sincronizados: number; erros: number }>(
        '/api/mercadolivre/vendas/importar', { empresaId });
      toast.success(`Importado! ${data.sincronizados} venda(s). ${data.erros} erro(s).`);
      carregar();
    } catch { toast.error('Erro ao importar.'); }
    finally  { setImporting(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="space-y-1">
          <Label className="text-xs">De</Label>
          <Input type="date" value={dtInicio} onChange={e => setDtInicio(e.target.value)} className="w-36" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Até</Label>
          <Input type="date" value={dtFim} onChange={e => setDtFim(e.target.value)} className="w-36" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="paid">Pago</SelectItem>
              <SelectItem value="shipped">Enviado</SelectItem>
              <SelectItem value="delivered">Entregue</SelectItem>
              <SelectItem value="cancelled">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={carregar} disabled={loading} className="gap-1">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}Filtrar
        </Button>
        <Button variant="outline" onClick={importar} disabled={importing} className="gap-1 ml-auto">
          {importing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          Importar do ML
        </Button>
      </div>

      {/* Totais */}
      {vendas.length > 0 && (
        <div className="flex gap-6 text-sm text-muted-foreground border rounded-lg p-3">
          <span><strong>{vendas.length}</strong> pedidos</span>
          <span className="text-green-600 font-medium">{fmt(vendas.reduce((s, v) => s + v.valorTotal, 0))} faturamento</span>
          <span className="text-red-500">{fmt(vendas.reduce((s, v) => s + v.valorTaxaML, 0))} taxas</span>
          <span className="font-medium">{fmt(vendas.reduce((s, v) => s + v.valorTotal - v.valorTaxaML, 0))} líquido</span>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {['Order ID', 'Comprador', 'Total', 'Frete', 'Taxa ML', 'Status', 'Envio', 'Data'].map(h => (
                <th key={h} className="text-left px-3 py-2 font-medium text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {vendas.length === 0
              ? <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">Nenhuma venda encontrada.</td></tr>
              : vendas.map(v => (
                <tr key={v.id} className="border-t hover:bg-muted/30">
                  <td className="px-3 py-2 font-mono text-xs">{v.orderId ?? '—'}</td>
                  <td className="px-3 py-2 max-w-[160px] truncate">{v.buyerNickname ?? v.buyerNome ?? '—'}</td>
                  <td className="px-3 py-2 font-medium">{fmt(v.valorTotal)}</td>
                  <td className="px-3 py-2">{fmt(v.valorFrete)}</td>
                  <td className="px-3 py-2 text-red-500">{fmt(v.valorTaxaML)}</td>
                  <td className="px-3 py-2">{statusBadge(v.statusPedido)}</td>
                  <td className="px-3 py-2">{statusBadge(v.statusEnvio)}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {new Date(v.dtVenda).toLocaleDateString('pt-BR')}
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Tab: Perguntas ────────────────────────────────────────────────────────

function TabPerguntas({ empresaId }: { empresaId: number }) {
  const [perguntas,  setPerguntas]  = useState<PerguntaML[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [filtro,     setFiltro]     = useState('todas');
  const [selecionada,setSelecionada]= useState<PerguntaML | null>(null);
  const [resposta,   setResposta]   = useState('');
  const [respondendo,setRespondendo]= useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filtro !== 'todas') params.status = filtro;
      const { data } = await apiClient.get<PerguntaML[]>(`/api/mercadolivre/perguntas/${empresaId}`, { params });
      setPerguntas(data);
    } catch { toast.error('Erro ao carregar perguntas.'); }
    finally  { setLoading(false); }
  };

  const responder = async () => {
    if (!selecionada?.perguntaId || !resposta.trim()) return;
    setRespondendo(true);
    try {
      await apiClient.post('/api/mercadolivre/perguntas/responder', {
        empresaId, perguntaId: selecionada.perguntaId, resposta: resposta.trim(),
      });
      toast.success('Pergunta respondida!');
      setResposta('');
      setSelecionada(null);
      carregar();
    } catch { toast.error('Erro ao responder.'); }
    finally  { setRespondendo(false); }
  };

  const pendentes = perguntas.filter(p => p.status === 'UNANSWERED').length;

  return (
    <div className="space-y-4">
      <div className="flex gap-2 items-center">
        <Select value={filtro} onValueChange={setFiltro}>
          <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas</SelectItem>
            <SelectItem value="UNANSWERED">Pendentes</SelectItem>
            <SelectItem value="ANSWERED">Respondidas</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={carregar} disabled={loading} className="gap-1">
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}Carregar
        </Button>
        {pendentes > 0 && (
          <Badge variant="destructive" className="ml-2">{pendentes} pendente(s)</Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {['Anúncio', 'Pergunta', 'Status', 'Data'].map(h => (
                  <th key={h} className="text-left px-3 py-2 font-medium text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {perguntas.length === 0
                ? <tr><td colSpan={4} className="text-center py-8 text-muted-foreground">Nenhuma pergunta encontrada.</td></tr>
                : perguntas.map(p => (
                  <tr key={p.id}
                    className={`border-t cursor-pointer hover:bg-muted/30 ${selecionada?.id === p.id ? 'bg-muted/50' : ''}`}
                    onClick={() => { setSelecionada(p); setResposta(p.resposta ?? ''); }}>
                    <td className="px-3 py-2 font-mono text-xs">{p.itemId ?? '—'}</td>
                    <td className="px-3 py-2 max-w-[200px] truncate">{p.pergunta ?? '—'}</td>
                    <td className="px-3 py-2">{statusBadge(p.status)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {new Date(p.dtPergunta).toLocaleDateString('pt-BR')}
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>

        {selecionada && (
          <Card>
            <CardHeader><CardTitle className="text-base">Responder Pergunta</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-muted-foreground">Pergunta</Label>
                <p className="mt-1 text-sm bg-muted/30 rounded p-2">{selecionada.pergunta}</p>
              </div>
              <div className="space-y-1">
                <Label>Resposta</Label>
                <Textarea
                  value={resposta}
                  onChange={e => setResposta(e.target.value)}
                  rows={4}
                  disabled={selecionada.status === 'ANSWERED'}
                  placeholder="Digite sua resposta..."
                />
              </div>
              {selecionada.status === 'UNANSWERED' && (
                <Button onClick={responder} disabled={respondendo || !resposta.trim()} className="gap-1 w-full">
                  {respondendo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  Enviar Resposta
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────

const MercadoLivre = () => {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id;

  if (!empresaId) return (
    <div className="p-6 text-muted-foreground">Empresa não identificada. Faça login novamente.</div>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-yellow-400 flex items-center justify-center">
          <ShoppingCart className="h-5 w-5 text-yellow-900" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-foreground">Mercado Livre</h1>
          <p className="text-muted-foreground">Gerencie anúncios, vendas e perguntas do seu marketplace</p>
        </div>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList>
          <TabsTrigger value="dashboard"  className="gap-1"><BarChart3 className="h-4 w-4" />Dashboard</TabsTrigger>
          <TabsTrigger value="anuncios"   className="gap-1"><Package className="h-4 w-4" />Anúncios</TabsTrigger>
          <TabsTrigger value="vendas"     className="gap-1"><ShoppingCart className="h-4 w-4" />Vendas</TabsTrigger>
          <TabsTrigger value="perguntas"  className="gap-1"><MessageSquare className="h-4 w-4" />Perguntas</TabsTrigger>
          <TabsTrigger value="config"     className="gap-1"><Settings className="h-4 w-4" />Configuração</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard"><Card><CardContent className="pt-6"><TabDashboard empresaId={empresaId} /></CardContent></Card></TabsContent>
        <TabsContent value="anuncios"><Card><CardContent className="pt-6"><TabAnuncios empresaId={empresaId} /></CardContent></Card></TabsContent>
        <TabsContent value="vendas"><Card><CardContent className="pt-6"><TabVendas empresaId={empresaId} /></CardContent></Card></TabsContent>
        <TabsContent value="perguntas"><Card><CardContent className="pt-6"><TabPerguntas empresaId={empresaId} /></CardContent></Card></TabsContent>
        <TabsContent value="config"><Card><CardContent className="pt-6"><TabConfig empresaId={empresaId} /></CardContent></Card></TabsContent>
      </Tabs>
    </div>
  );
};

export default MercadoLivre;
